/* PMT predictive search (NSL-004 v2) — <pmt-predictive-search>.
   PMT-owned combobox + Section Rendering API results loader. Core mechanics
   modelled on Dawn's predictive-search.js (debounced input → fetch
   routes.predictive_search_url with section_id → inject server-rendered
   markup → ARIA combobox/listbox with aria-activedescendant), improved with
   per-keystroke AbortController and a small query cache. Dawn's script is
   never loaded. Instances parameterise their ids, so the modal and the drawer
   host can coexist without duplicate DOM ids. */
(function () {
  'use strict';

  var DEBOUNCE_MS = 300;
  var MIN_CHARS = 2;
  var CACHE_MAX = 8;

  class PmtPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this._pmtInit) return;
      this._pmtInit = true;

      this.input = this.querySelector('.pmt-search__input');
      this.results = this.querySelector('[data-pmt-search-results]');
      this.status = this.querySelector('[data-pmt-search-status]');
      this.searchUrl = this.getAttribute('data-pmt-search-url');
      this.sectionId = this.getAttribute('data-pmt-section-id') || 'pmt-predictive-search';

      if (!this.input) return;

      this._abort = null;
      this._debounce = null;
      this._cache = new Map();
      this._activeIndex = -1;
      this._options = [];
      this._ac = new AbortController();
      var signal = this._ac.signal;

      this.input.addEventListener('input', this.onInput.bind(this), { signal });
      this.input.addEventListener('keydown', this.onKeydown.bind(this), { signal });
      this.input.addEventListener('focusout', this.onFocusOut.bind(this), { signal });
      document.addEventListener('click', this.onDocumentClick.bind(this), { signal });
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();
      if (this._debounce) clearTimeout(this._debounce);
      if (this._abort) this._abort.abort();
      /* allow connectedCallback to re-wire if the same node is reattached
         (e.g. a theme editor section move) after the aborts above */
      this._pmtInit = false;
    }

    onInput() {
      if (this._debounce) clearTimeout(this._debounce);

      var term = this.input.value.trim();
      if (term.length < MIN_CHARS) {
        this.closeResults(true);
        return;
      }

      var component = this;
      this._debounce = setTimeout(function () {
        component.query(term);
      }, DEBOUNCE_MS);
    }

    query(term) {
      var component = this;
      var cached = this._cache.get(term);
      if (cached) {
        this.render(cached.html, cached.count);
        return;
      }

      if (this._abort) this._abort.abort();
      this._abort = new AbortController();
      this.setAttribute('data-pmt-loading', '');

      var params =
        'q=' +
        encodeURIComponent(term) +
        '&resources[type]=product,page&resources[limit]=5&resources[limit_scope]=each&section_id=' +
        this.sectionId;

      fetch(this.searchUrl + '?' + params, { signal: this._abort.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (markup) {
          var doc = new DOMParser().parseFromString(markup, 'text/html');
          var fresh = doc.querySelector('[data-pmt-predictive-results]');
          var payload = {
            html: fresh ? fresh.innerHTML : '',
            count: fresh ? fresh.getAttribute('data-pmt-count') || '0' : '0'
          };
          if (component._cache.size >= CACHE_MAX) {
            component._cache.delete(component._cache.keys().next().value);
          }
          component._cache.set(term, payload);
          component.removeAttribute('data-pmt-loading');
          component.render(payload.html, payload.count);
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          component.removeAttribute('data-pmt-loading');
        });
    }

    render(html, count) {
      if (!this.results) return;
      this.results.innerHTML = html;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('data-pmt-open', '');

      this._options = Array.prototype.slice.call(this.results.querySelectorAll('[role="option"]'));
      var idPrefix = (this.input.id || 'pmt-search') + '-option-';
      this._options.forEach(function (option, index) {
        option.id = idPrefix + index;
      });
      this._activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');

      if (this.status) {
        var template = this.status.getAttribute('data-count-template');
        this.status.textContent = template ? template.replace('[count]', count || '0') : count || '';
      }
    }

    closeResults(clearActive) {
      if (this._abort) this._abort.abort();
      if (this.results && !this.results.hidden) {
        this.results.hidden = true;
        this.results.innerHTML = '';
      }
      this.input.setAttribute('aria-expanded', 'false');
      this.removeAttribute('data-pmt-open');
      this.input.removeAttribute('aria-activedescendant');
      if (clearActive) this._activeIndex = -1;
      if (this.status) this.status.textContent = '';
    }

    onFocusOut(event) {
      if (!this.results || this.results.hidden) return;
      if (!this.contains(event.relatedTarget) && event.relatedTarget !== this.input) {
        this.closeResults(true);
      }
    }

    onDocumentClick(event) {
      if (!this.results || this.results.hidden) return;
      if (!this.contains(event.target) || event.target.classList.contains('pmt-search__drawer-overlay')) {
        this.closeResults(true);
      }
    }

    onKeydown(event) {
      var open = this.results && !this.results.hidden && this._options.length > 0;
      var lastIndex = this._options.length - 1;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!open) {
            this.onInput();
            return;
          }
          this.setActiveIndex(this._activeIndex < lastIndex ? this._activeIndex + 1 : 0);
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!open) return;
          this.setActiveIndex(this._activeIndex > 0 ? this._activeIndex - 1 : lastIndex);
          break;
        case 'Home':
          if (!open) return;
          event.preventDefault();
          this.setActiveIndex(0);
          break;
        case 'End':
          if (!open) return;
          event.preventDefault();
          this.setActiveIndex(lastIndex);
          break;
        case 'Enter':
          if (open && this._activeIndex >= 0) {
            event.preventDefault();
            var link = this._options[this._activeIndex].querySelector('a');
            if (link) link.click();
          }
          break;
        case 'Escape':
          if (open || this.input.value !== '') {
            event.stopPropagation();
            this.input.value = '';
            this.closeResults(true);
          }
          break;
      }
    }

    setActiveIndex(index) {
      this._activeIndex = index;
      this._options.forEach(function (option, optionIndex) {
        option.classList.toggle('pmt-is-active', optionIndex === index);
        option.setAttribute('aria-selected', optionIndex === index ? 'true' : 'false');
      });
      var active = this._options[index];
      if (active) {
        this.input.setAttribute('aria-activedescendant', active.id);
        if (typeof active.scrollIntoView === 'function') {
          active.scrollIntoView({ block: 'nearest' });
        }
      }
    }
  }

  if (!customElements.get('pmt-predictive-search')) {
    customElements.define('pmt-predictive-search', PmtPredictiveSearch);
  }
})();
